import { inject, injectable } from 'inversify';

import { BOOKINGKIT_API_FACTORY, SUPPLIER_STATE_SERVICE } from '@/bootstrap/ServiceProviders';
import { BookingKitApiFactoryInterface } from '@/models/Factories/BookingKitApiFactoryInterface';
import { UseSupplierInterface } from '@/state/supplier';
import { AppServiceError } from '@/services/AppsService/Errors/AppServiceError';
import { fromApiApp, fromApiAgent } from '@/services/AppsService/Reducers';
import { BkAppType, WidgetAgent } from '@/services/AppsService/types';

@injectable()
export class BkAppsService {
    private connection: ReturnType<BookingKitApiFactoryInterface['getConnection']>;

    private supplierId: string;

    constructor(
        @inject(BOOKINGKIT_API_FACTORY) factory: BookingKitApiFactoryInterface,
        @inject(SUPPLIER_STATE_SERVICE) useSupplierState: () => UseSupplierInterface,
    ) {
        const { supplier } = useSupplierState();
        if (!supplier.value?.id) {
            throw AppServiceError.fromMissingSupplier();
        }
        this.supplierId = supplier.value.id;
        this.connection = factory.getConnection();
        this.listApps = this.listApps.bind(this);
        this.getAppResources = this.getAppResources.bind(this);
    }

    async listApps(pageSize = 10): Promise<BkAppType[]> {
        const { supplierId } = this;
        const { data } = await this.connection.listApps({vendorId:supplierId, pageSize});
        if (!data) {
            throw AppServiceError.fromMalformedResponse('data');
        }
        const appsList = data.data;
        if (!appsList) {
            throw AppServiceError.fromMalformedResponse('data.data');
        }
        return appsList.map(fromApiApp);
    }

    async getAppResources(appId: string, resourceType: 'WIDGET_AGENT', pageSize = 100): Promise<WidgetAgent[]> {
        const { supplierId } = this;
        const { data } = await this.connection.resourceList({
            vendorId: supplierId, appId, resourceType, pageSize,
        });
        if (!data) {
            throw AppServiceError.fromMalformedResponse('data');
        }
        if (!data.data) {
            throw AppServiceError.fromMalformedResponse('data.data');
        }
        return data.data.map(fromApiAgent) ?? [];
    }

    async writeAppResources(payload:{appId: string, resourceType: 'WIDGET_AGENT', resourceId: string, widgetPublishableKey: string}): Promise<WidgetAgent> {
        const { supplierId } = this;
        const { appId, resourceType, resourceId, widgetPublishableKey } = payload;
        const { data } = await this.connection.resourceWrite({
            vendorId: supplierId,
            appId,
            resourceType,
            resourceId,
            updateAppResourceRequest: {
                widget_publishable_key: [widgetPublishableKey],
            },
        });
        if (!data) {
            throw AppServiceError.fromMalformedResponse('data');
        }
        return fromApiAgent(data);
    }

    async activateApp(appId: string): Promise<BkAppType> {
        const { data } = await this.connection.appActivate({
            vendorId: this.supplierId,
            appId,
        });
        if (!data) {
            throw AppServiceError.fromMalformedResponse('data');
        }
        return fromApiApp(data);
    }
}

export default { BkAppsService };
